import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";

import {
  QUERY_TEST_OUTLOOK_APP_ONLY,
  QUERY_CLIENT_WITH_OUTLOOK_SUBSCRIPTION,
} from "../../../../config/graphql/query";
import { CONNECT_OUTLOOK_APP_ONLY } from "../../../../config/graphql/mutation";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

import {
  clearConsentDraft,
  saveConsentDraft,
  IOutlookConsentDraft,
} from "./consentDraft";

interface ConnectAppOnlyModalProps {
  clientId: string;
  visible: boolean;
  onHide: () => void;
  /** Filled in when the page was reached by returning from the consent screen. */
  initial?: IOutlookConsentDraft | null;
  consentGranted?: boolean;
  consentError?: string | null;
}

// Entra needs up to a minute to spread a fresh consent: retried quietly.
const AUTO_RETRIES = 6;
const AUTO_RETRY_MS = 10000;

/**
 * Connecting with application permissions is three deliberate steps, in order:
 * the customer's admin consents once in their own tenant, we verify that the
 * consent and the mailbox really work, and only then is anything created.
 *
 * The test is what makes this safe: nothing is written until Graph has actually
 * answered for that mailbox, so a wrong tenant id or a mailbox outside their
 * access policy is a message on this form rather than a broken integration.
 */
const ConnectAppOnlyModal = React.memo(
  ({
    clientId,
    visible,
    onHide,
    initial,
    consentGranted,
    consentError,
  }: ConnectAppOnlyModalProps) => {
    const { t } = useTranslation(["client", "common"]);

    const [tenantId, setTenantId] = useState("");
    const [mailbox, setMailbox] = useState("");
    const [title, setTitle] = useState("");
    const [tested, setTested] = useState<IOutlookAppOnlyTestResult | null>(
      null,
    );
    const [error, setError] = useState<string | null>(null);

    // The return from Microsoft is a fresh page load, so the test is fired once
    // by itself — otherwise the form looks identical to the one the admin left
    // and the obvious reading is that nothing happened.
    const autoTested = useRef(false);
    const autoRetries = useRef(0);

    const apiUrl = getApiUrl();

    const consentUrl = useMemo(
      () =>
        qs.stringifyUrl(
          {
            url: `${apiUrl}/api/v1/outlook/auth/admin-consent`,
            query: {
              client: clientId,
              tenantId: tenantId.trim(),
              redirectUri: window.location.href,
            },
          },
          { encode: true },
        ),
      [apiUrl, clientId, tenantId],
    );

    const [runTest, { loading: testing }] = useLazyQuery<{
      testOutlookAppOnly: IOutlookAppOnlyTestResult;
    }>(QUERY_TEST_OUTLOOK_APP_ONLY, {
      fetchPolicy: "network-only",
      onCompleted: ({ testOutlookAppOnly }) => {
        setTested(testOutlookAppOnly);
        setError(
          testOutlookAppOnly.ok ? null : testOutlookAppOnly.message ?? null,
        );
      },
      onError: (e) => {
        setTested(null);
        setError(e.message);
      },
    });

    const [connect, { loading: connecting }] = useMutation(
      CONNECT_OUTLOOK_APP_ONLY,
      {
        refetchQueries: [
          {
            query: QUERY_CLIENT_WITH_OUTLOOK_SUBSCRIPTION,
            variables: { id: clientId },
          },
        ],
        onCompleted: () => {
          clearConsentDraft(clientId);
          onHide();
        },
        onError: (e) => {
          // A failed attempt voids the earlier test.
          setTested(null);
          setError(e.message);
        },
      },
    );

    const canTest = !!tenantId.trim() && !!mailbox.trim() && !testing;

    // Any edit invalidates a previous result — otherwise someone could test one
    // mailbox and create another.
    const onChange = useCallback(
      (setter: (value: string) => void) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setter(event.target.value);
          setTested(null);
          setError(null);
        },
      [],
    );

    const onTest = useCallback(() => {
      setError(null);
      runTest({
        variables: { tenantId: tenantId.trim(), mailbox: mailbox.trim() },
      });
    }, [runTest, tenantId, mailbox]);

    const onConnect = useCallback(() => {
      setError(null);
      connect({
        variables: {
          input: {
            tenantId: tenantId.trim(),
            mailbox: mailbox.trim(),
            ...(title.trim() ? { title: title.trim() } : {}),
          },
        },
      });
    }, [connect, tenantId, mailbox, title]);

    const onClose = useCallback(() => {
      setTested(null);
      setError(null);
      clearConsentDraft(clientId);
      onHide();
    }, [onHide, clientId]);

    // Coming back from the consent screen: refill what was typed before leaving
    // and report a refusal, which Entra only ever tells us in the URL.
    useEffect(() => {
      if (!initial) {
        return;
      }

      if (initial.tenantId) setTenantId(initial.tenantId);
      if (initial.mailbox) setMailbox(initial.mailbox);
      if (initial.title) setTitle(initial.title);

      setError(consentError ?? null);
      autoTested.current = false;
      autoRetries.current = 0;
    }, [initial, consentError]);

    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        consentError ||
        autoTested.current ||
        !tenantId.trim() ||
        !mailbox.trim()
      ) {
        return;
      }

      autoTested.current = true;
      runTest({
        variables: { tenantId: tenantId.trim(), mailbox: mailbox.trim() },
      });
    }, [visible, consentGranted, consentError, tenantId, mailbox, runTest]);

    // A refusal right after the consent is usually Entra still catching up.
    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        !tested ||
        tested.ok ||
        autoRetries.current >= AUTO_RETRIES
      ) {
        return undefined;
      }

      autoRetries.current += 1;

      const timer = window.setTimeout(() => {
        runTest({
          variables: { tenantId: tenantId.trim(), mailbox: mailbox.trim() },
        });
      }, AUTO_RETRY_MS);

      return () => window.clearTimeout(timer);
    }, [visible, consentGranted, tested, tenantId, mailbox, runTest]);

    // No red while the quiet retries still run.
    const autoChecking =
      consentGranted &&
      !tested?.ok &&
      (testing || (!!tested && autoRetries.current < AUTO_RETRIES));

    return (
      <Modal show={visible} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.outlook.appOnly.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {t("client:client.integrations.outlook.appOnly.modal.intro")}
          </p>

          <h6 className="mb-1">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.oneTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.oneText",
            )}
          </p>

          <Form.Group>
            <Form.Label>
              {t("client:client.integrations.outlook.appOnly.field.tenantId")}
            </Form.Label>
            <Form.Control
              value={tenantId}
              onChange={onChange(setTenantId)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <Form.Text className="text-muted">
              {t(
                "client:client.integrations.outlook.appOnly.field.tenantIdHelp",
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              {t("client:client.integrations.outlook.appOnly.field.mailbox")}
            </Form.Label>
            <Form.Control
              value={mailbox}
              onChange={onChange(setMailbox)}
              placeholder="reception@customer.com"
            />
            <Form.Text className="text-muted">
              {t(
                "client:client.integrations.outlook.appOnly.field.mailboxHelp",
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              {t("client:client.integrations.outlook.appOnly.field.title")}
            </Form.Label>
            <Form.Control value={title} onChange={onChange(setTitle)} />
            <Form.Text className="text-muted">
              {t("client:client.integrations.outlook.appOnly.field.titleHelp")}
            </Form.Text>
          </Form.Group>

          <hr className="my-3" />

          <h6 className="mb-1">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.twoTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.twoText",
            )}
          </p>

          <div className="d-flex align-items-center mb-3">
            <a
              href={consentUrl}
              // Microsoft sends the admin back to a reloaded CMS page, so what
              // is on the form has to survive outside React.
              onClick={() =>
                saveConsentDraft(clientId, {
                  tenantId: tenantId.trim(),
                  mailbox: mailbox.trim(),
                  title: title.trim(),
                })
              }
              className={`btn btn-outline-primary mr-3 text-nowrap flex-shrink-0 ${
                tenantId.trim() ? "" : "disabled"
              }`}
            >
              {t("client:client.integrations.outlook.appOnly.button.consent")}
            </a>
            <small className="text-muted">
              {t(
                "client:client.integrations.outlook.appOnly.button.consentHelp",
              )}
            </small>
          </div>

          <hr className="my-3" />

          <h6 className="mb-1">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.threeTitle",
            )}
          </h6>
          <p className="text-muted small mb-2">
            {t(
              "client:client.integrations.outlook.appOnly.modal.steps.threeText",
            )}
          </p>

          {autoChecking && (
            <Alert variant="info" className="small py-2 mb-2">
              {t("client:client.integrations.outlook.appOnly.consentReturned")}
            </Alert>
          )}

          {tested?.ok && (
            <Alert variant="success">
              {t("client:client.integrations.outlook.appOnly.tested", {
                name: tested.displayName,
                email: tested.email,
              })}
            </Alert>
          )}

          {error && !autoChecking && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outline-primary"
            disabled={!canTest}
            onClick={onTest}
          >
            {testing && (
              <Spinner animation="border" size="sm" className="mr-2" />
            )}
            {t("client:client.integrations.outlook.appOnly.button.test")}
          </Button>
          <Button
            variant="primary"
            // Deliberately gated on a passing test: the mutation runs the same
            // check server-side, but failing it there would mean a confusing
            // error instead of a form that says what is wrong.
            disabled={!tested?.ok || connecting}
            onClick={onConnect}
          >
            {connecting && (
              <Spinner animation="border" size="sm" className="mr-2" />
            )}
            {t("client:client.integrations.outlook.appOnly.button.connect")}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  },
);

export default ConnectAppOnlyModal;
